import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Role } from '@texas/api/endpoints/userApi';
import { useActiveSession } from '@texas/hooks/useSession';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { useNavigationStore } from '@texas/components/navigation/hooks/useNavigationStore';
import { configurationNavigationStyles } from '@texas/resources/theme/styles/configuration';

interface ConfigurationNavigationProps {
  organizationName: string;
}

interface ConfigurationItem {
  labelKey: string;
  route: string;
}

const systemAdministratorItems: ConfigurationItem[] = [
  {
    labelKey: 'configuration.navigation.articleTemplates',
    route: 'configuration.articleTemplates',
  },
  {
    labelKey: 'configuration.navigation.articleTags',
    route: 'configuration.articleTags',
  },
  {
    labelKey: 'configuration.navigation.branches',
    route: 'configuration.branches',
  },
  {
    labelKey: 'configuration.navigation.branchRegions',
    route: 'configuration.branchRegions',
  },
  {
    labelKey: 'configuration.navigation.countries',
    route: 'configuration.countries',
  },
  {
    labelKey: 'configuration.navigation.currencies',
    route: 'configuration.currencies',
  },
  {
    labelKey: 'configuration.navigation.customsCodes',
    route: 'configuration.customsCodes',
  },
  {
    labelKey: 'configuration.navigation.matrixGroups',
    route: 'configuration.matrixGroups',
  },
  {
    labelKey: 'configuration.navigation.matrixNodes',
    route: 'configuration.matrixNodes',
  },
  {
    labelKey: 'configuration.navigation.deliveryMethod',
    route: 'configuration.deliveryMethods',
  },
  {
    labelKey: 'configuration.navigation.deliveryTerms',
    route: 'configuration.deliveryTerms',
  },
  {
    labelKey: 'configuration.navigation.paymentTerms',
    route: 'configuration.paymentTerms',
  },
  {
    labelKey: 'configuration.navigation.freightCosts',
    route: 'configuration.freightCosts',
  },
  {
    labelKey: 'configuration.navigation.handlingFees',
    route: 'configuration.handlingFees',
  },
  {
    labelKey: 'configuration.navigation.materials',
    route: 'configuration.materials',
  },
  {
    labelKey: 'configuration.navigation.productGroups',
    route: 'configuration.productGroups',
  },
  {
    labelKey: 'configuration.navigation.complianceDocumentDefinitions',
    route: 'configuration.complianceDocumentDefinitions',
  },
  {
    labelKey: 'configuration.navigation.techniques',
    route: 'configuration.techniques',
  },
  {
    labelKey: 'configuration.navigation.vatCodes',
    route: 'configuration.vatCodes',
  },
  {
    labelKey: 'configuration.navigation.treatments',
    route: 'configuration.treatments',
  },
  {
    labelKey: 'configuration.navigation.dimensionGroups',
    route: 'configuration.dimensionGroups',
  },
  {
    labelKey: 'configuration.navigation.vdcTemplates',
    route: 'configuration.vdcTemplates',
  },
  {
    labelKey: 'configuration.navigation.vdcTiers',
    route: 'configuration.vdcTiers',
  },
  {
    labelKey: 'configuration.navigation.internalApi',
    route: 'configuration.internalApi',
  },
  {
    labelKey: 'configuration.navigation.qualities',
    route: 'configuration.qualities',
  },
  {
    labelKey: 'configuration.navigation.organization',
    route: 'configuration.organization',
  },
  {
    labelKey: 'configuration.navigation.garp',
    route: 'configuration.garp',
  },
];

const garpItem = systemAdministratorItems.find(
  (item) => item.route === 'configuration.garp',
)!;

function isStateActive(activeState: string, route: string) {
  return activeState === route || activeState.startsWith(`${route}.`);
}

export function ConfigurationNavigation({
  organizationName,
}: ConfigurationNavigationProps) {
  const { t } = useTranslation();
  const { activeState } = useNavigationStore();
  const { currentUser } = useActiveSession();
  const [filter, setFilter] = useState('');
  const isSystemAdministrator = hasRole(currentUser.role, [
    Role.SystemAdministrator,
  ]);
  const normalizedFilter = filter.trim().toLowerCase();
  const items = (isSystemAdministrator ? systemAdministratorItems : [garpItem])
    .map((item) => ({
      ...item,
      label:
        item.route === 'configuration.organization'
          ? organizationName
          : t(item.labelKey as 'configuration.navigation.garp'),
    }))
    .filter((item) => item.label.toLowerCase().includes(normalizedFilter));

  return (
    <Flex
      as="nav"
      aria-label={t('configuration.navigation.label')}
      sx={configurationNavigationStyles.root}
    >
      <Flex sx={configurationNavigationStyles.items}>
        <Text as="h2" sx={configurationNavigationStyles.header}>
          {t('actionBar.configuration')}
        </Text>
        <Box mb={3} flexShrink={0}>
          <SearchFilterInput
            value={filter}
            onChange={setFilter}
            placeholder={t('configuration.navigation.search')}
            style="entityDirectory"
            height="38px"
            debounceDelay={0}
          />
        </Box>
        {items.length === 0 && (
          <Text color="navigation.contextMuted" fontSize="sm" px={2.5} py={2}>
            {t('general.noResult')}
          </Text>
        )}
        {items.map((item) => {
          const active = isStateActive(activeState, item.route);

          return (
            <AngularNavLink
              key={item.route}
              to={item.route}
              navobj={{}}
              aria-current={active ? 'page' : undefined}
              flexShrink={0}
              variant={active ? 'contextNavigationActive' : 'contextNavigation'}
            >
              <Text noOfLines={1}>{item.label}</Text>
            </AngularNavLink>
          );
        })}
      </Flex>
    </Flex>
  );
}
